<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { api } from '@/lib/http.js'
import { formatDate, formatMoney } from '@/lib/format.js'
import { reportError } from '@/composables/useSave.js'
import { retryFilling } from '@/lib/saveFilling.js'
import { useMeta } from '@/stores/meta.js'
import { useUi } from '@/stores/ui.js'
import UiButton from '@/components/ui/UiButton.vue'
import RecordLink from '@/components/record/RecordLink.vue'

/**
 * What one step of the work suggests for another record (D-184), the person's decision: an
 * offer sent — its opportunity to the proposal and its value; accepted — won (or its value
 * corrected, or an extension); no longer binding — lost, or a contact planned; its acceptance
 * undone — open again; an opportunity won — its account a customer; an agreement to reprice — an
 * opportunity for it.
 *
 * Applied, or rejected for good; closed with ×, only hidden here — on the record it is about it
 * shows again. Shown for a record (`subject`), from a record (`source`), or all mine (`mine`).
 */
const props = defineProps({ subject: { type: Object, default: null }, source: { type: Object, default: null }, mine: Boolean })
const emit = defineEmits(['applied'])
const { t } = useI18n()
const meta = useMeta()
const ui = useUi()
const items = ref([])
const hidden = ref(new Set())
const busy = ref(null)
const losing = ref(null) // {uuid, reason}

async function load() {
  try {
    if (props.mine) {
      items.value = (await api('/suggestions/mine')).data
      return
    }
    const query = {}
    if (props.subject?.uuid) query.subject = `${props.subject.entity}:${props.subject.uuid}`
    if (props.source?.uuid) query.source = `${props.source.entity}:${props.source.uuid}`
    items.value = Object.keys(query).length ? (await api('/suggestions', { query })).data : []
  } catch {
    items.value = []
  }
}
// Each by itself: one array of them is a new one at every render of the page (a key typed in an
// offer), and would load again each time.
watch([() => props.subject?.uuid, () => props.source?.uuid, () => props.mine], load, { immediate: true })
defineExpose({ reload: load })

const shown = computed(() => items.value.filter((s) => !hidden.value.has(s.uuid)))
const money = (v, s) => formatMoney(v, s.context?.currency || meta.baseCurrency)
// Said about another record than the one shown: named, with a link.
const elsewhere = (s) => s.subject && !(props.subject?.uuid === s.subject.uuid)

function text(s) {
  const c = s.context || {}
  const o = s.options || []
  switch (s.kind) {
    case 'offer_sent': {
      const parts = [o[0]?.stage ? t('suggestions.stage', { stage: o[0].stage.label }) : null, o[0]?.estimated_value != null ? t('suggestions.value', { value: money(o[0].estimated_value, s) }) : null].filter(Boolean)
      return t('suggestions.text.offer_sent', { offer: c.offer, revision: c.revision, what: parts.join(t('suggestions.and')) })
    }
    case 'offer_accepted':
      return o[0]?.key === 'win' ? t('suggestions.text.offer_accepted_open', { offer: c.offer, revision: c.revision, value: money(o[0].value, s) }) : t('suggestions.text.offer_accepted_won', { offer: c.offer, revision: c.revision })
    case 'offer_closed':
      return t(`suggestions.text.offer_closed_${c.status === 'rejected' ? 'rejected' : 'expired'}`, { offer: c.offer })
    case 'acceptance_undone':
      return t('suggestions.text.acceptance_undone', { offer: c.offer, revision: c.revision })
    case 'opportunity_won':
      return t('suggestions.text.opportunity_won', { opportunity: c.opportunity })
    case 'agreement_reprice':
      return t('suggestions.text.agreement_reprice', { agreement: c.agreement, date: c.date })
    default:
      return ''
  }
}
function label(s, option) {
  switch (`${s.kind}:${option.key}`) {
    case 'offer_accepted:update': return t('suggestions.options.update', { value: money(option.value, s) })
    case 'offer_accepted:extension': return t('suggestions.options.extension', { value: money(option.value, s) })
    case 'agreement_reprice:opportunity': return option.estimated_value != null ? t('suggestions.options.reprice', { value: money(option.estimated_value, s), date: formatDate(option.close_date) }) : t('suggestions.options.reprice_plain')
    default: return t(`suggestions.options.${s.kind}.${option.key}`)
  }
}

async function apply(s, option, extra = {}) {
  if (s.kind === 'offer_closed' && option.key === 'lose' && !extra.lost_reason) {
    losing.value = { uuid: s.uuid, reason: option.lost_reason || '' }
    return
  }
  busy.value = `${s.uuid}:${option.key}`
  try {
    // Refused for fields its record must have (D-343): asked for, saved to it, applied again.
    const run = async () => (await api(`/suggestions/${s.uuid}/apply`, { method: 'POST', body: { option: option.key, ...extra } })).data
    const body = s.subject?.uuid ? await retryFilling(s.subject.entity, s.subject.uuid, run) : await run()
    if (!body) return
    losing.value = null
    ui.success(body.made ? t('suggestions.made', { label: body.made.label }) : t('suggestions.applied'))
    items.value = items.value.filter((x) => x.uuid !== s.uuid)
    emit('applied', { suggestion: s, made: body.made })
  } catch (e) {
    reportError(e, { entity: s.subject?.entity })
  } finally {
    busy.value = null
  }
}
async function reject(s) {
  busy.value = `${s.uuid}:reject`
  try {
    await api(`/suggestions/${s.uuid}/reject`, { method: 'POST' })
    items.value = items.value.filter((x) => x.uuid !== s.uuid)
  } catch (e) {
    reportError(e)
  } finally {
    busy.value = null
  }
}
const hide = (s) => (hidden.value = new Set([...hidden.value, s.uuid]))
</script>

<template>
  <div v-if="shown.length" class="space-y-2" data-testid="suggestions">
    <div v-for="s in shown" :key="s.uuid" class="flex flex-wrap items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm dark:border-emerald-800 dark:bg-emerald-950/30" :data-testid="`suggestion-${s.kind}`">
      <i class="fa-solid fa-lightbulb text-emerald-600" aria-hidden="true"></i>
      <p v-private class="min-w-0 flex-1">
        <template v-if="elsewhere(s)"><RecordLink class="link font-medium" :entity="s.subject.entity" :uuid="s.subject.uuid">{{ s.subject.label }}</RecordLink>: </template>{{ text(s) }}
      </p>
      <template v-if="losing?.uuid === s.uuid">
        <select v-model="losing.reason" class="input w-auto py-1 text-sm" data-testid="suggestion-lost-reason">
          <option value="">{{ $t('suggestions.lostReason') }}</option>
          <option v-for="e in meta.dictionary('lost_reason').entries" :key="e.key" :value="e.key">{{ meta.localized(e.label) }}</option>
        </select>
        <UiButton size="sm" variant="danger" :disabled="!losing.reason" :loading="busy === `${s.uuid}:lose`" data-testid="suggestion-lose-confirm" @click="apply(s, { key: 'lose' }, { lost_reason: losing.reason })">{{ $t('suggestions.options.offer_closed.lose') }}</UiButton>
        <UiButton size="sm" variant="ghost" @click="losing = null">{{ $t('common.cancel') }}</UiButton>
      </template>
      <template v-else>
        <UiButton v-for="o in s.options" :key="o.key" size="sm" :variant="o.key === s.options[0].key ? 'primary' : 'secondary'" :loading="busy === `${s.uuid}:${o.key}`" :data-testid="`suggestion-apply-${o.key}`" @click="apply(s, o)">{{ label(s, o) }}</UiButton>
        <UiButton size="sm" variant="ghost" :loading="busy === `${s.uuid}:reject`" :title="$t('suggestions.rejectHint')" data-testid="suggestion-reject" @click="reject(s)">{{ $t('suggestions.reject') }}</UiButton>
      </template>
      <button type="button" class="rounded p-1 text-muted hover:bg-[var(--surface-3)]" :title="$t('suggestions.hideHint')" :aria-label="$t('common.close')" data-testid="suggestion-hide" @click="hide(s)"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
    </div>
  </div>
</template>
